import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowLeft, Volume2, VolumeX, Play, Pause, Download, Send, Settings, 
  Clock, History, Mic, Radio, Bell, ShoppingCart, Users, Gavel, 
  Award, Gift, MapPin, RefreshCw, Trash2, Plus, Calendar, ChevronDown
} from 'lucide-react';
import api from '../../utils/api';
import { toast } from 'sonner';

const VoiceControlPanel = () => {
  const [settings, setSettings] = useState({
    is_enabled: true,
    auto_announce_orders: true,
    auto_announce_activations: true,
    auto_announce_commissions: false,
    auto_announce_referrals: false,
    auto_announce_auctions: true,
    auto_announce_p2p: false,
    auto_announce_franchise: true,
    auto_announce_ranks: true,
    voice_speed: 1.0,
    language: 'hinglish'
  });
  const [activeTab, setActiveTab] = useState('quick');
  const [customMessage, setCustomMessage] = useState('');
  const [isPlaying, setIsPlaying] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [currentAudio, setCurrentAudio] = useState(null);
  const [history, setHistory] = useState([]);
  const [scheduled, setScheduled] = useState([]);
  const [stats, setStats] = useState({});
  const [presetParams, setPresetParams] = useState({});
  const audioRef = useRef(null);

  const presets = [
    { id: 'welcome', name: 'Welcome Message', icon: Bell, color: 'bg-blue-500', params: [] },
    { id: 'order', name: 'New Order', icon: ShoppingCart, color: 'bg-green-500', 
      params: ['customer_name', 'order_total', 'product_count'] },
    { id: 'activation', name: 'Activation', icon: Award, color: 'bg-yellow-500', 
      params: ['user_name', 'pack_name'] },
    { id: 'wallet_credit', name: 'Wallet Credit', icon: Gift, color: 'bg-purple-500', 
      params: ['user_name', 'amount', 'wallet_type'] },
    { id: 'commission', name: 'Commission Earned', icon: Users, color: 'bg-emerald-500', 
      params: ['user_name', 'amount', 'from_user'] },
    { id: 'listing', name: 'New P2P Listing', icon: MapPin, color: 'bg-orange-500', 
      params: ['seller_name', 'listing_title', 'price'] },
    { id: 'auction_live', name: 'Auction Live', icon: Gavel, color: 'bg-red-500', 
      params: ['auction_title', 'current_bid'] },
    { id: 'auction_ending', name: 'Auction Ending', icon: Clock, color: 'bg-amber-500', 
      params: ['auction_title', 'current_bid', 'time_remaining'] },
    { id: 'franchise', name: 'New Franchise', icon: MapPin, color: 'bg-indigo-500', 
      params: ['user_name', 'franchise_type', 'location'] },
    { id: 'rank', name: 'Rank Achievement', icon: Award, color: 'bg-pink-500', 
      params: ['user_name', 'rank_name'] },
    { id: 'referral', name: 'New Referral', icon: Users, color: 'bg-cyan-500', 
      params: ['referrer_name', 'new_user_name'] },
  ];

  useEffect(() => {
    fetchSettings();
    fetchHistory();
    fetchScheduled();
    fetchStats();
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await api.get('/admin/voice-control/settings');
      setSettings(res.data);
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    }
  };

  const fetchHistory = async () => {
    try {
      const res = await api.get('/admin/voice-control/history?limit=20');
      setHistory(res.data);
    } catch (error) {
      console.error('Failed to fetch history:', error);
    }
  };

  const fetchScheduled = async () => {
    try {
      const res = await api.get('/admin/voice-control/scheduled');
      setScheduled(res.data);
    } catch (error) {
      console.error('Failed to fetch scheduled:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const res = await api.get('/admin/voice-control/stats');
      setStats(res.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const saveSettings = async () => {
    try {
      await api.put('/admin/voice-control/settings', settings);
      toast.success('Settings saved!');
    } catch (error) {
      toast.error('Failed to save settings');
    }
  };

  const generateCustomAnnouncement = async () => {
    if (!customMessage.trim()) {
      toast.error('Please enter a message');
      return;
    }
    
    setIsGenerating(true);
    try {
      const res = await api.post('/admin/voice-control/announce', {
        message: customMessage,
        event_type: 'custom'
      });
      
      if (res.data.audio) {
        playAudio(res.data.audio);
        setCurrentAudio(res.data.audio);
        toast.success('Announcement generated!');
        fetchHistory();
        fetchStats();
      }
    } catch (error) {
      toast.error('Failed to generate announcement');
    } finally {
      setIsGenerating(false);
    }
  };

  const generatePresetAnnouncement = async (presetId) => {
    setIsGenerating(true);
    try {
      const params = presetParams[presetId] || {};
      const res = await api.post(`/admin/voice-control/announce/preset/${presetId}`, params);
      
      if (res.data.audio) {
        playAudio(res.data.audio);
        setCurrentAudio(res.data.audio);
        toast.success('Announcement generated!');
        fetchHistory();
        fetchStats();
      }
    } catch (error) {
      toast.error('Failed to generate announcement');
    } finally {
      setIsGenerating(false);
    }
  };

  const playAudio = (base64Audio) => {
    if (audioRef.current) {
      audioRef.current.pause();
    }
    
    const audio = new Audio(`data:audio/mp3;base64,${base64Audio}`);
    audioRef.current = audio;
    
    audio.onplay = () => setIsPlaying(true);
    audio.onended = () => setIsPlaying(false);
    audio.onpause = () => setIsPlaying(false);
    
    audio.play();
  };

  const togglePlay = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
    }
  };

  const downloadAudio = async () => {
    if (!currentAudio) {
      toast.error('Generate an announcement first');
      return;
    }
    
    const link = document.createElement('a');
    link.href = `data:audio/mp3;base64,${currentAudio}`;
    link.download = `priya_announcement_${Date.now()}.mp3`;
    link.click();
    toast.success('Audio downloaded!');
  };

  const updatePresetParam = (presetId, param, value) => {
    setPresetParams(prev => ({
      ...prev,
      [presetId]: {
        ...(prev[presetId] || {}),
        [param]: value
      }
    }));
  };

  return (
    <div className="min-h-screen bg-background p-4 md:p-6">
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/admin" className="p-2 hover:bg-white/10 rounded-lg">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div>
              <h1 className="text-2xl font-bold flex items-center gap-2">
                <Volume2 className="w-6 h-6 text-primary" />
                Voice Control Panel
              </h1>
              <p className="text-muted-foreground">Priya - Hindi Voice Announcements</p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <div className={`px-3 py-1 rounded-full text-sm ${settings.is_enabled ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
              {settings.is_enabled ? 'Active' : 'Disabled'}
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="glass-card p-4 text-center">
            <p className="text-3xl font-bold text-primary">{stats.total_announcements || 0}</p>
            <p className="text-sm text-muted-foreground">Total Announcements</p>
          </div>
          <div className="glass-card p-4 text-center">
            <p className="text-3xl font-bold text-green-400">{stats.today_count || 0}</p>
            <p className="text-sm text-muted-foreground">Today</p>
          </div>
          <div className="glass-card p-4 text-center">
            <p className="text-3xl font-bold text-yellow-400">{stats.scheduled_active || 0}</p>
            <p className="text-sm text-muted-foreground">Scheduled</p>
          </div>
          <div className="glass-card p-4 text-center">
            <p className="text-3xl font-bold text-purple-400">Hindi</p>
            <p className="text-sm text-muted-foreground">Language</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 border-b border-white/10 pb-2">
          {[
            { id: 'quick', name: 'Quick Announce', icon: Mic },
            { id: 'presets', name: 'Presets', icon: Radio },
            { id: 'scheduled', name: 'Scheduled', icon: Calendar },
            { id: 'history', name: 'History', icon: History },
            { id: 'settings', name: 'Settings', icon: Settings },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                activeTab === tab.id ? 'bg-primary text-black' : 'hover:bg-white/10'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              <span className="hidden md:inline">{tab.name}</span>
            </button>
          ))}
        </div>

        {/* Quick Announce Tab */}
        {activeTab === 'quick' && (
          <div className="glass-card p-6 space-y-6">
            <h2 className="text-xl font-semibold">Quick Announcement</h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-muted-foreground mb-2">
                  Enter your message (Hindi/Hinglish recommended)
                </label>
                <textarea
                  value={customMessage}
                  onChange={(e) => setCustomMessage(e.target.value)}
                  placeholder="Anant Web Portal par aapka swagat hai! Aaj humari special sale chal rahi hai..."
                  className="w-full h-32 bg-white/5 border border-white/10 rounded-lg p-4 resize-none"
                />
              </div>
              
              <div className="flex items-center gap-4">
                <button
                  onClick={generateCustomAnnouncement}
                  disabled={isGenerating}
                  className="btn-primary flex items-center gap-2"
                >
                  {isGenerating ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                  Generate & Play
                </button>
                
                {currentAudio && (
                  <>
                    <button
                      onClick={togglePlay}
                      className="p-3 bg-white/10 rounded-full hover:bg-white/20"
                    >
                      {isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
                    </button>
                    
                    <button
                      onClick={downloadAudio}
                      className="p-3 bg-white/10 rounded-full hover:bg-white/20"
                      title="Download MP3"
                    >
                      <Download className="w-5 h-5" />
                    </button>
                  </>
                )}
              </div>
            </div>
            
            {/* Sample Messages */}
            <div>
              <h3 className="text-sm font-medium text-muted-foreground mb-2">Quick Templates</h3>
              <div className="flex flex-wrap gap-2">
                {[
                  "Anant Web Portal par aapka swagat hai!",
                  "Humari nayi sale abhi live hai! Jaldi karo!",
                  "Aaj ka special offer miss mat karo!",
                  "Badhaai ho! Aapne naya rank achieve kiya!",
                ].map((msg, i) => (
                  <button
                    key={i}
                    onClick={() => setCustomMessage(msg)}
                    className="px-3 py-1 bg-white/5 hover:bg-white/10 rounded-full text-sm"
                  >
                    {msg.slice(0, 30)}...
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Presets Tab */}
        {activeTab === 'presets' && (
          <div className="glass-card p-6 space-y-6">
            <h2 className="text-xl font-semibold">Preset Announcements</h2>
            
            <div className="grid md:grid-cols-2 gap-4">
              {presets.map(preset => (
                <div key={preset.id} className="bg-white/5 rounded-xl p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg ${preset.color}`}>
                        <preset.icon className="w-5 h-5 text-white" />
                      </div>
                      <span className="font-medium">{preset.name}</span>
                    </div>
                    <button
                      onClick={() => generatePresetAnnouncement(preset.id)}
                      disabled={isGenerating}
                      className="p-2 bg-primary/20 hover:bg-primary/30 rounded-lg text-primary"
                    >
                      <Play className="w-4 h-4" />
                    </button>
                  </div>
                  
                  {preset.params.length > 0 && (
                    <div className="space-y-2">
                      {preset.params.map(param => (
                        <input
                          key={param}
                          type={param.includes('amount') || param.includes('price') || param.includes('bid') || param.includes('count') ? 'number' : 'text'}
                          placeholder={param.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                          value={presetParams[preset.id]?.[param] || ''}
                          onChange={(e) => updatePresetParam(preset.id, param, e.target.value)}
                          className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm"
                        />
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Scheduled Tab */}
        {activeTab === 'scheduled' && (
          <div className="glass-card p-6 space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold">Scheduled Announcements</h2>
              <button className="btn-primary flex items-center gap-2">
                <Plus className="w-4 h-4" />
                Add Schedule
              </button>
            </div>
            
            {scheduled.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <Calendar className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No scheduled announcements</p>
              </div>
            ) : (
              <div className="space-y-3">
                {scheduled.map(item => (
                  <div key={item.id} className="bg-white/5 rounded-lg p-4 flex items-center justify-between">
                    <div>
                      <p className="font-medium">{item.message.slice(0, 50)}...</p>
                      <p className="text-sm text-muted-foreground">{item.scheduled_time}</p>
                    </div>
                    <button className="p-2 hover:bg-white/10 rounded-lg text-red-400">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* History Tab */}
        {activeTab === 'history' && (
          <div className="glass-card p-6 space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold">Announcement History</h2>
              <button 
                onClick={fetchHistory}
                className="p-2 hover:bg-white/10 rounded-lg"
              >
                <RefreshCw className="w-4 h-4" />
              </button>
            </div>
            
            {history.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <History className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No announcements yet</p>
              </div>
            ) : (
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {history.map(item => (
                  <div key={item.id} className="bg-white/5 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="px-2 py-1 bg-primary/20 text-primary rounded text-xs">
                        {item.preset_type || item.event_type || 'custom'}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {new Date(item.created_at).toLocaleString()}
                      </span>
                    </div>
                    <p className="text-sm">{item.message}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Settings Tab */}
        {activeTab === 'settings' && (
          <div className="glass-card p-6 space-y-6">
            <h2 className="text-xl font-semibold">Voice Settings</h2>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-white/5 rounded-lg">
                <div>
                  <p className="font-medium">Voice System</p>
                  <p className="text-sm text-muted-foreground">Enable/disable all voice announcements</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settings.is_enabled}
                    onChange={(e) => setSettings({...settings, is_enabled: e.target.checked})}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-white/20 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                </label>
              </div>
              
              <div className="grid md:grid-cols-2 gap-4">
                {[
                  { key: 'auto_announce_orders', label: 'Auto-announce Orders' },
                  { key: 'auto_announce_activations', label: 'Auto-announce Activations' },
                  { key: 'auto_announce_commissions', label: 'Auto-announce Commissions' },
                  { key: 'auto_announce_referrals', label: 'Auto-announce Referrals' },
                  { key: 'auto_announce_auctions', label: 'Auto-announce Auctions' },
                  { key: 'auto_announce_p2p', label: 'Auto-announce P2P Deals' },
                  { key: 'auto_announce_franchise', label: 'Auto-announce Franchise' },
                  { key: 'auto_announce_ranks', label: 'Auto-announce Rank Achievements' },
                ].map(item => (
                  <div key={item.key} className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                    <span className="text-sm">{item.label}</span>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={settings[item.key]}
                        onChange={(e) => setSettings({...settings, [item.key]: e.target.checked})}
                        className="sr-only peer"
                      />
                      <div className="w-9 h-5 bg-white/20 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-primary"></div>
                    </label>
                  </div>
                ))}
              </div>
              
              <div className="flex items-center gap-4 p-4 bg-white/5 rounded-lg">
                <div className="flex-1">
                  <p className="font-medium mb-2">Voice Speed</p>
                  <input
                    type="range"
                    min="0.5"
                    max="1.5"
                    step="0.1"
                    value={settings.voice_speed}
                    onChange={(e) => setSettings({...settings, voice_speed: parseFloat(e.target.value)})}
                    className="w-full"
                  />
                </div>
                <span className="text-lg font-mono">{settings.voice_speed}x</span>
              </div>
              
              <button
                onClick={saveSettings}
                className="btn-primary w-full"
              >
                Save Settings
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VoiceControlPanel;
